import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import PublicLayout from "../shell/PublicLayout";
import ClassMedia from "../classpage/ClassMedia";
import Overline from "../components/Overline";
import { PlayIcon } from "../components/icons";
import { usePlayer } from "../player/PlayerProvider";
import { ragSimilar } from "../../service/ragSearch";
import { getPositionSeconds } from "../api/positionSeconds";
import { fmt } from "../player/MiniPlayer";
import { paths } from "../routing/paths";

/*
 * Redesigned class page (Class Page reference) — the SEO landing surface:
 * breadcrumbs + position, series/progress chips, H1, one-paragraph context,
 * metadata line — all above the fold. Player card + source-text panel
 * side-by-side, tags, prev/next in series, series context card + related
 * rail (RAG /similar supplies series position, prev/next, and related).
 */

export const tagPill =
  "text-[14px] font-semibold leading-5 text-ic-body bg-white border border-ic-borderGray px-4 rounded-3xl no-underline min-h-[44px] box-border inline-flex items-center gap-1.5 transition-all duration-150 ease-out hover:border-ic-blue hover:text-ic-blue hover:no-underline focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft";

/* Split a tag like "Beinoni (בינוני)" so the Hebrew renders RTL and slate.
   Shared with TagPage, where the same split styles the shelf's H1. */
export function TagLabel({ name, hebrewClass = "text-ic-slate" }) {
  const m = /^(.*?)\s*(\([֐-׿][^)]*\))\s*$/.exec(name || "");
  if (!m) return <>{name}</>;
  return (
    <>
      {m[1]}
      <span dir="rtl" className={hebrewClass}>{m[2]}</span>
    </>
  );
}

export default function ClassPage({ classData }) {
  const player = usePlayer();
  const [similar, setSimilar] = useState(null);
  const livePosition = useSelector((s) => s.player.position);
  const current = useSelector((s) => s.player.current);

  useEffect(() => {
    let cancelled = false;
    setSimilar(null);
    if (classData.id) {
      ragSimilar(classData.id, 8)
        .then((r) => !cancelled && setSimilar(r))
        .catch(() => !cancelled && setSimilar(null));
    }
    return () => {
      cancelled = true;
    };
  }, [classData.id]);

  const series = similar?.series || null;
  const related = similar?.classes || [];

  // Record which media URL belongs to this class slug (device-local progress
  // fallback), and pull synced account positions — fetchPositions merges
  // furthest-wins into localStorage, then the bump re-renders the player card
  // so it resumes from the synced position.
  const [, setSyncTick] = useState(0);
  useEffect(() => {
    const url = classData.audio?.src || classData.audio?.streamUrl || classData.video?.src;
    if (url && classData.slug) {
      try {
        localStorage.setItem(`ic-class-media:${classData.slug}`, url);
      } catch (_) {}
    }
    import("../api/progressSync").then(({ fetchPositions, isSignedIn }) => {
      if (isSignedIn()) fetchPositions().then(() => setSyncTick((t) => t + 1));
    });
  }, [classData]);

  // Queue up-next in the series for the sheet + auto-advance.
  useEffect(() => {
    if (series?.next) {
      player.setQueue([
        {
          title: series.next.title,
          seriesTitle: series.title,
          mediaKind: "audio",
          src: null, // resolved when played via its page; queue play navigates
          href: paths.permalink(series.next.permalink || series.next.class_id),
          indexLabel: String(series.position + 1).padStart(2, "0"),
          metaLabel: "Next in series",
          navigateOnly: true,
        },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series?.next?.class_id]);

  const mediaUrl = classData.audio?.src || classData.audio?.streamUrl || classData.video?.src;
  const isCurrent = Boolean(current) && (current.src === mediaUrl || current.classId === classData.id);
  const savedPos = getPositionSeconds(mediaUrl);
  const positionSeconds = isCurrent ? livePosition : savedPos;
  const durationSeconds = classData.audio?.durationSeconds || 0;
  const progressPct = durationSeconds > 0 ? Math.round((positionSeconds / durationSeconds) * 100) : 0;

  const contextParagraph = useMemo(() => {
    const p = classData.paragraphs[0];
    if (!p) return null;
    const text = p.text || "";
    return text.length > 400 ? `${text.slice(0, 400).trim()}…` : text;
  }, [classData.paragraphs]);

  const metaLine = [
    durationSeconds ? fmt(durationSeconds) : null,
    [classData.audio && "Audio", classData.video && "Video", classData.files.length > 0 && "source text"]
      .filter(Boolean)
      .join(" + "),
    classData.dateLabel,
    "Rabbi Yossi Paltiel",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <PublicLayout>
      <main className="max-w-[1000px] w-full mx-auto px-5 pt-5 flex flex-col gap-5">
        {/* Breadcrumbs + position */}
        <nav aria-label="Breadcrumb" className="ic-rail flex items-center gap-1 overflow-x-auto text-[14px] leading-5 whitespace-nowrap">
          <Link to={paths.home()} className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline">
            Home
          </Link>
          {series && (
            <>
              <span className="text-ic-slate">→</span>
              <Link
                to={`/series/${series.permalink || series.series_id}`}
                className="text-ic-blue font-semibold py-3 px-1 -my-3 inline-flex items-center min-h-[44px] hover:underline"
              >
                {series.title}
              </Link>
              <span className="text-ic-slate">→</span>
              <span className="text-ic-body font-semibold px-1">
                Class {series.position} of {series.total}
              </span>
            </>
          )}
        </nav>

        {/* Title block — above the fold */}
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {series && (
              <Link
                to={`/series/${series.permalink || series.series_id}`}
                className="relative bg-ic-lightGray text-ic-body text-[12px] font-semibold tracking-[0.5px] px-3 py-1.5 rounded-xl no-underline inline-flex items-center min-h-[28px] hover:text-ic-blue hover:no-underline before:absolute before:-inset-y-2 before:inset-x-0 before:content-['']"
              >
                {series.title}
              </Link>
            )}
            {progressPct > 0 && (
              <span className="bg-ic-lightGray text-ic-blue text-[12px] font-bold tracking-[0.5px] px-3 py-1.5 rounded-xl inline-flex items-center gap-1">
                <PlayIcon size={10} /> {Math.min(progressPct, 100)}% played
              </span>
            )}
          </div>
          <h1 className="m-0 font-extrabold text-black tracking-[-0.5px] ic-wrap-anywhere" style={{ fontSize: "clamp(26px, 6vw, 40px)", lineHeight: 1.2 }}>
            {classData.title}
          </h1>
          {contextParagraph && (
            <p className="m-0 text-[18px] font-normal leading-7 text-ic-body">{contextParagraph}</p>
          )}
          <span className="text-[14px] leading-5 text-ic-slate">{metaLine}</span>
        </section>

        {/* Player, with the source text opening beside it (ClassMedia) */}
        <ClassMedia classData={classData} seriesInfo={series} />

        {/* About this class — remaining paragraphs */}
        {classData.paragraphs.length > 1 && (
          <section className="flex flex-col gap-3">
            <Overline>About this class</Overline>
            {classData.paragraphs.slice(1).map((p, i) => (
              <p key={i} className="m-0 text-[18px] leading-7 text-ic-body ic-wrap-anywhere">
                {p.text}
              </p>
            ))}
          </section>
        )}

        {/* Tags */}
        {classData.tags.length > 0 && (
          <section className="flex flex-col gap-3">
            <Overline>Tags — explore related topics</Overline>
            <div className="flex gap-2 flex-wrap">
              {classData.tags.map((t) => (
                <Link key={t.id} to={paths.tag(t.slug)} className={tagPill}>
                  <TagLabel name={t.name} />
                </Link>
              ))}
            </div>
            <p className="m-0 text-[14px] leading-5 text-ic-slate">
              Each tag opens its topic shelf; glossary terms link to their "start here" classes.
            </p>
          </section>
        )}

        {/* Prev / next in series */}
        {series && (
          <section className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))" }}>
            {series.prev ? (
              <Link
                to={paths.permalink(series.prev.permalink || series.prev.class_id)}
                className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised px-5 py-4 flex items-center gap-3 no-underline hover:no-underline transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#186CCE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="text-[12px] font-bold tracking-[0.5px] uppercase text-ic-slate">
                    Previous · Class {series.position - 1} of {series.total}
                  </span>
                  <span className="text-[16px] font-semibold leading-6 text-black ic-wrap-anywhere">{series.prev.title}</span>
                </div>
              </Link>
            ) : (
              <div aria-disabled="true" className="bg-ic-offwhite border border-dashed border-ic-borderGray rounded-xl px-5 py-4 flex items-center gap-3 text-ic-disabledText">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                  <path d="M15 6l-6 6 6 6" />
                </svg>
                <div className="flex flex-col gap-0.5 min-w-0">
                  <span className="text-[12px] font-bold tracking-[0.5px] uppercase">Previous</span>
                  <span className="text-[16px] font-semibold leading-6">This is the first class in the series</span>
                </div>
              </div>
            )}
            {series.next ? (
              <Link
                to={paths.permalink(series.next.permalink || series.next.class_id)}
                className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised px-5 py-4 flex items-center gap-3 no-underline hover:no-underline transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
              >
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <span className="text-[12px] font-bold tracking-[0.5px] uppercase text-ic-slate">
                    Next · Class {series.position + 1} of {series.total}
                  </span>
                  <span className="text-[16px] font-semibold leading-6 text-black ic-wrap-anywhere">{series.next.title}</span>
                </div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#186CCE" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </Link>
            ) : (
              <div aria-disabled="true" className="bg-ic-offwhite border border-dashed border-ic-borderGray rounded-xl px-5 py-4 flex items-center gap-3 text-ic-disabledText">
                <div className="flex-1 flex flex-col gap-0.5 min-w-0">
                  <span className="text-[12px] font-bold tracking-[0.5px] uppercase">Next</span>
                  <span className="text-[16px] font-semibold leading-6">This is the last class in the series</span>
                </div>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="flex-shrink-0">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </div>
            )}
          </section>
        )}

        {/* Series context + related */}
        {(series || related.length > 0) && (
          <section className="flex flex-col gap-3 pb-10">
            <h2 className="m-0 font-bold text-black tracking-[-0.3px]" style={{ fontSize: "clamp(22px, 5vw, 28px)", lineHeight: 1.3 }}>
              {series ? "In this series" : "Related classes"}
            </h2>
            {series && (
              <Link
                to={`/series/${series.permalink || series.series_id}`}
                className="bg-ic-navy border border-white/10 rounded-xl shadow-ic-raised-dark p-5 flex items-center gap-4 no-underline hover:no-underline transition-all duration-150 ease-out hover:shadow-ic-elevated-dark focus-visible:outline-none focus-visible:shadow-ic-focus-dark"
              >
                <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-ic-navyCard hidden xs:flex flex-col items-center justify-center">
                  <span dir="rtl" className="text-[20px] font-extrabold text-ic-sky leading-6">סדרה</span>
                </div>
                <div className="flex-1 min-w-0 flex flex-col gap-1.5">
                  <span className="text-[16px] font-semibold leading-6 text-white ic-wrap-anywhere">{series.title}</span>
                  <span className="text-[12px] leading-4 text-ic-lightGray">
                    Class {series.position} of {series.total}
                  </span>
                </div>
                <span className="flex-shrink-0 text-ic-sky text-[14px] font-semibold underline">View series</span>
              </Link>
            )}
            {related.length > 0 && (
              <div className="ic-rail flex gap-4 overflow-x-auto py-2 items-stretch">
                {related.slice(0, 8).map((c) => (
                  <Link
                    key={c.class_id}
                    to={paths.permalink(c.permalink || c.class_id)}
                    className="bg-white border border-ic-lightGray rounded-xl shadow-ic-raised px-5 py-4 no-underline hover:no-underline flex flex-col gap-2 transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:shadow-ic-focus-soft"
                    style={{ flex: "0 0 clamp(220px, 62vw, 300px)" }}
                  >
                    <span className="self-start bg-ic-lightGray text-ic-body text-[11px] font-semibold tracking-[0.5px] px-2.5 py-0.5 rounded-xl">
                      Related
                    </span>
                    <span className="text-[16px] font-semibold leading-6 text-black ic-wrap-anywhere">{c.title}</span>
                    {c.summary && (
                      <span className="mt-auto text-[14px] leading-5 text-ic-slate overflow-hidden" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
                        {c.summary}
                      </span>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </section>
        )}
      </main>
    </PublicLayout>
  );
}
